/*eslint-disable*/
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { HeadCell } from '../../models/headCell';

interface Props {
  columns: HeadCell[];
  data: any;
  sortProps?: {
    sortBy: string;
    sortType: 'asc' | 'desc';
    onRequestSort: (property: string) => void;
  };
  paginationProps?: {
    page: number;
    rowsPerPage: number;
    totalElements?: number;
    onChangePage: (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      page: number
    ) => void;
    onChangeRowsPerPage: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
  };
  actionProps?: {
    onDelete?: (event: React.MouseEvent<unknown>, data: any) => void;
    onEdit?: (event: React.MouseEvent<unknown>, data: any) => void;
    onDeactivate?: (event: React.MouseEvent<unknown>, data: any) => void;
    onActivate?: (event: React.MouseEvent<unknown>, data: any) => void;
    showDeleteButton?: (data: any) => boolean;
    showEditButton?: (data: any) => boolean;
    showActivateButton?: (data: any) => boolean;
    showDeactivateButton?: (data: any) => boolean;
    style?: React.CSSProperties;
  };
  rowClick?: (data: any) => void;
  checkBoxes?: {
    selectRows?: (data: any) => void;
    showCheckBox?: (data: any) => boolean;
  };
  children?: React.ReactNode;
}

const DefaultTable = (props: Props): JSX.Element => {
  const {
    columns,
    data,
    sortProps,
    paginationProps,
    actionProps,
    rowClick,
    checkBoxes
  } = props;

  const [selected, setSelected] = useState<any[]>([]);

  const showHideCheckbox = (data: any): boolean => {
    if (checkBoxes?.showCheckBox) {
      return checkBoxes?.showCheckBox(data);
    }
    return true;
  };

  const isSelected = (row: any) => selected.indexOf(row) !== -1;

  const handleClick = (event: React.MouseEvent<unknown>, row: any) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(row);
    let newSelected: any[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
    if (checkBoxes?.selectRows) {
      checkBoxes.selectRows(newSelected);
    }
  };

  const getDataBasedConditions = () => {
    if (checkBoxes?.showCheckBox) {
      return data.filter((row: any) => showHideCheckbox(row));
    }
    return data;
  };

  useEffect(() => {
    setSelected([]);
  }, [data]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(getDataBasedConditions());
      if (checkBoxes?.selectRows) {
        checkBoxes.selectRows(getDataBasedConditions());
      }
      return;
    }
    setSelected([]);
    if (checkBoxes?.selectRows) {
      checkBoxes.selectRows([]);
    }
  };

  return (
    <Box sx={{ minWidth: 650, overflow: 'hidden' }}>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead sx={{ width: '100%' }}>
            <TableRow>
              {data && checkBoxes?.selectRows && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < getDataBasedConditions().length
                    }
                    checked={
                      getDataBasedConditions().length > 0 &&
                      selected.length === getDataBasedConditions().length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{
                      'aria-label': 'select all desserts'
                    }}
                  />
                </TableCell>
              )}
              {columns.map(
                (column) =>
                  column.visible && (
                    <TableCell
                      key={column.id}
                      sx={{ ...column.thStyle, whiteSpace: 'nowrap' }}
                    >
                      {column.sortable && sortProps ? (
                        <TableSortLabel
                          active={column.id === sortProps.sortBy}
                          direction={
                            sortProps.sortBy === column.id
                              ? sortProps.sortType
                              : 'asc'
                          }
                          onClick={() => sortProps.onRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  )
              )}
              {actionProps && (
                <TableCell align="right" sx={{ width: '120px' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row: any, index: number) => {
              const isRowSelected = isSelected(row);
              const labelId = `enhanced-table-checkbox-${row.id}`;

              return (
                <TableRow
                  key={row.id ?? index}
                  hover
                  aria-checked={isRowSelected}
                  tabIndex={-1}
                  selected={isRowSelected}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => (rowClick ? rowClick(row) : null)}
                >
                  {checkBoxes?.selectRows && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        onClick={(event) => handleClick(event, row)}
                        color="primary"
                        disabled={!showHideCheckbox(row)}
                        checked={isRowSelected}
                        inputProps={{
                          'aria-labelledby': labelId
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map(
                    (column) =>
                      column.visible && (
                        <TableCell key={column.id} sx={column.tdStyle}>
                          {column.render(row) ? column.render(row) : '--'}
                        </TableCell>
                      )
                  )}
                  {actionProps && (
                    <TableCell
                      sx={{
                        ...actionProps.style
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {actionProps.showEditButton &&
                          actionProps.showEditButton(row) && (
                            <IconButton
                              sx={{ mr: { md: 1 } }}
                              onClick={(event) => {
                                actionProps.onEdit &&
                                  actionProps.onEdit(event, row);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                        {actionProps.showDeleteButton &&
                          actionProps.showDeleteButton(row) && (
                            <IconButton
                              onClick={(event) => {
                                actionProps.onDelete &&
                                  actionProps.onDelete(event, row);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        {actionProps.showDeactivateButton &&
                          actionProps.showDeactivateButton(row) && (
                            <IconButton
                              onClick={(event) => {
                                actionProps.onDeactivate &&
                                  actionProps.onDeactivate(event, row);
                              }}
                            >
                              <BlockIcon />
                            </IconButton>
                          )}
                        {actionProps.showActivateButton &&
                          actionProps.showActivateButton(row) && (
                            <IconButton
                              onClick={(event) => {
                                actionProps.onActivate &&
                                  actionProps.onActivate(event, row);
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                          )}
                        {actionProps.showDeleteButton &&
                          actionProps.showEditButton &&
                          !actionProps.showDeleteButton(row) &&
                          !actionProps.showEditButton(row) && (
                            <Box sx={{ textAlign: 'right' }}>--</Box>
                          )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {!!data?.length && paginationProps && (
        <Table>
          <TableFooter sx={{ width: '100%' }}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[
                  50,
                  100,
                  200,
                  {
                    label: 'All',
                    value: paginationProps.totalElements ?? 0
                  }
                ]}
                count={paginationProps.totalElements ?? 0}
                rowsPerPage={paginationProps.rowsPerPage}
                page={paginationProps.page}
                onPageChange={paginationProps.onChangePage}
                onRowsPerPageChange={(event) =>
                  paginationProps.onChangeRowsPerPage(event)
                }
              />
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </Box>
  );
};

export default DefaultTable;
